import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { MessagePartPrimitive, type ImageMessagePart } from '@assistant-ui/react';
import { artifactDownloadUrl } from '@/entities/file';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { DotMatrix } from '@/shared/ui/dot-matrix';
import { downloadBlob } from '../model/markdownExport';
import { AssistantFilePart } from './AssistantFilePart';

const ARTIFACT_DOWNLOAD_PATH_RE = /\/api\/v1\/artifacts\/(?<artifactId>[^/]+)\/download\/?$/;

type AuthenticatedImageMetadata = {
  providerMetadata?: {
    agenthub?: {
      artifactUrl?: string;
      artifactId?: string;
      contentType?: string;
      sizeBytes?: number;
    };
  };
};

function internalArtifactId(src: string): string | null {
  try {
    const url = new URL(src, window.location.origin);
    return url.pathname.match(ARTIFACT_DOWNLOAD_PATH_RE)?.groups?.artifactId ?? null;
  } catch {
    return src.match(ARTIFACT_DOWNLOAD_PATH_RE)?.groups?.artifactId ?? null;
  }
}

function fetchUrl(src: string): string {
  if (/^https?:\/\//i.test(src)) return src;
  return new URL(src, readEnv('VITE_API_BASE_URL') ?? window.location.origin).toString();
}

function isInternalArtifactImage(src: string): boolean {
  return internalArtifactId(src) !== null;
}

export function AuthenticatedImagePart(part: ImageMessagePart) {
  const agenthub = (part as ImageMessagePart & AuthenticatedImageMetadata).providerMetadata?.agenthub;
  const sourceUrl = agenthub?.artifactUrl ?? part.image;
  const authenticatedArtifact = Boolean(agenthub?.artifactUrl) || isInternalArtifactImage(sourceUrl);
  const [imageUrl, setImageUrl] = useState<string | null>(authenticatedArtifact ? null : sourceUrl);
  const [imageFailed, setImageFailed] = useState(false);
  const filename = part.filename ?? 'image';
  const artifactId = agenthub?.artifactId ?? internalArtifactId(sourceUrl);

  useEffect(() => {
    if (!authenticatedArtifact) {
      setImageUrl(sourceUrl);
      setImageFailed(false);
      return undefined;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const response = await fetch(fetchUrl(sourceUrl), {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (cancelled) return;
        if (!response.ok) {
          setImageFailed(true);
          return;
        }
        objectUrl = URL.createObjectURL(await response.blob());
        if (!cancelled) {
          setImageUrl(objectUrl);
          setImageFailed(false);
        }
      } catch {
        if (!cancelled) setImageFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [authenticatedArtifact, sourceUrl]);

  async function handleDownload() {
    try {
      const token = getToken();
      const response = await fetch(authenticatedArtifact ? fetchUrl(sourceUrl) : sourceUrl, {
        credentials: authenticatedArtifact ? 'include' : undefined,
        headers: authenticatedArtifact && token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) return;
      downloadBlob(await response.blob(), filename);
    } catch {
      // Keep image rendering intact if an explicit download attempt fails.
    }
  }

  if (imageFailed && artifactId) {
    return (
      <AssistantFilePart
        type="data"
        name="file"
        data={{
          fileId: artifactId,
          filename,
          contentType: agenthub?.contentType ?? 'image/*',
          sizeBytes: agenthub?.sizeBytes,
          downloadUrl: artifactDownloadUrl('', artifactId),
        }}
      />
    );
  }

  if (imageFailed) {
    return (
      <a
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mb-2 inline-flex max-w-full items-center gap-1.5 text-primary underline underline-offset-2 hover:no-underline"
      >
        <span className="truncate">{filename}</span>
        <Download className="size-3.5 shrink-0" />
      </a>
    );
  }

  if (!imageUrl) {
    return (
      <div className="mb-2 flex h-40 w-64 max-w-full items-center justify-center rounded-md border border-border/60 bg-muted/40">
        <DotMatrix state="loading" label={`Loading ${filename}`} />
      </div>
    );
  }

  return (
    <figure className="mb-2 max-w-full">
      <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="block w-fit max-w-full">
        <MessagePartPrimitive.Image
          src={imageUrl}
          alt={filename}
          className="max-h-96 max-w-full cursor-zoom-in rounded-md border border-border/60 object-contain"
        />
      </a>
      <figcaption className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="truncate">{filename}</span>
        <button
          type="button"
          aria-label={`Download ${filename}`}
          onClick={() => void handleDownload()}
          className="ml-auto rounded p-1 hover:bg-accent hover:text-accent-foreground"
        >
          <Download className="size-3.5" />
        </button>
      </figcaption>
    </figure>
  );
}
